import { SheetPrimitive } from '@watersport/ui-react-next'
import React, { useCallback, useEffect, useImperativeHandle, useState } from 'react'

import { INITIAL_MAP_ZOOM_LEVEL } from 'constant'
import { useLayout } from 'contexts/LayoutProvider'
import { useSearch } from 'contexts/SearchProvider'
import CustomMapControls from 'features/common/components/CustomMapControls'
import MapWrapper from 'features/common/components/MapWrapper'
import { cn } from 'features/common/helpers/cn'
import { getMyLocation } from 'features/common/helpers/getMyLocation'
import { useMultiModals } from 'features/common/hooks/useMultiModals'
import { SearchScope } from 'features/common/types'
import DrawingMapView from 'features/locationMap/components/DrawingMapView'
import SelectDrawingMode from 'features/locationMap/components/SelectDrawingMode'
import SelectMapView from 'features/locationMap/components/SelectMapView'

export interface LocationMapProps {
  show: boolean
  polygons?: [number, number][][]
  onClose?: () => void
  onFinish: (type: 'select' | 'drawing', scope: SearchScope | [number, number][][]) => void
}

export interface LocationMapInterface {
  setInitialScope: (scope: SearchScope) => void
  setInitialPolygons: (polygons: [number, number][][]) => void
}

const LocationMap: React.ForwardRefRenderFunction<LocationMapInterface, LocationMapProps> = (
  { show, polygons, onClose, onFinish },
  ref,
) => {
  const [scope, setScope] = useState<SearchScope | undefined>(undefined)
  const selectMapView = React.useRef<React.ElementRef<typeof SelectMapView>>(null)
  const drawingMapView = React.useRef<React.ElementRef<typeof DrawingMapView>>(null)
  const { mapDrawingMode, setMapDrawingMode } = useLayout()
  const { sheetRef } = useMultiModals(show)

  const onClickMyLocation = useCallback(() => {
    if (selectMapView) selectMapView.current?.onClickMyLocation()
  }, [])

  const handleClose = useCallback(() => {
    if (onClose) onClose()
  }, [onClose])

  const onSearchFromSelect = useCallback(() => {
    if (!scope) return
    onFinish('select', scope)
    handleClose()
  }, [handleClose, onFinish, scope])

  const onSearchFromDrawing = useCallback(
    (p: [number, number][][]) => {
      onFinish('drawing', p)
      handleClose()
    },
    [handleClose, onFinish],
  )

  useImperativeHandle(
    ref,
    () => ({
      setInitialScope(s: SearchScope) {
        setScope(s)
        selectMapView.current?.setInitialScope(s)
      },
      setInitialPolygons(p: [number, number][][]) {
        drawingMapView.current?.setInitialPolygons(p)
      },
    }),
    [],
  )

  const { myLocation } = useSearch()

  useEffect(() => {
    if (myLocation) {
      setScope({ center: myLocation, distance: 5, placeName: '' })
    } else {
      getMyLocation().then(c => {
        setScope({ center: c.coordinate, distance: 5, placeName: c.region ?? '' })
      })
    }
  }, [myLocation, setScope])

  return (
    <SheetPrimitive.Sheet open={show}>
      <SheetPrimitive.SheetContent
        side={'bottom'}
        className={cn(
          'p-0 m-0 h-full border-none bg-blue-900 flex flex-col gap-0 w-full max-w-md mx-auto md:max-w-md',
        )}
        overlayClassName={'bg-gray-foreground-400'}
        ref={sheetRef}
      >
        <MapWrapper defaultZoom={INITIAL_MAP_ZOOM_LEVEL} center={scope?.center}>
          {!mapDrawingMode && (
            <SelectDrawingMode goBack={handleClose} selectDrawingMode={setMapDrawingMode} />
          )}
          {mapDrawingMode === 'select' && (
            <SelectMapView
              scope={scope}
              setScope={setScope}
              onClose={handleClose}
              onSearch={onSearchFromSelect}
              onChangeViewMode={setMapDrawingMode}
              ref={selectMapView}
            />
          )}
          {mapDrawingMode === 'drawing' && (
            <DrawingMapView
              polygons={polygons}
              goBack={handleClose}
              onChangeViewMode={setMapDrawingMode}
              onSearch={onSearchFromDrawing}
              ref={drawingMapView}
            />
          )}
          {mapDrawingMode && (
            <div className="absolute bottom-[100px] right-5">
              <CustomMapControls showMyLocation={true} onClickMyLocation={onClickMyLocation} />
            </div>
          )}
        </MapWrapper>
      </SheetPrimitive.SheetContent>
    </SheetPrimitive.Sheet>
  )
}

export default React.forwardRef(LocationMap)
